import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import {
  SupportAction,
  SupportCasePayload,
  approveRefund,
  getSupportCase,
  rejectRefund,
  requestRefund,
} from '../api/support';
import { formatMoney } from '../../../commodity/format/money';
import {
  Empty,
  Field,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Section,
  Table,
  Value,
} from '../../../design-system/ops';

/**
 * PI-3 Epic 3 S3.1 + S3.4 + S3.7 — everything known about one enrollment,
 * for the operator holding its case.
 *
 * Rewritten onto the design system. It had its own accordion, its own action
 * card, its own button factory and its own input styling, plus five hard-coded
 * colours; none of it reachable by the console's theme.
 *
 * Three things were fixed while it was open.
 *
 * The disputes section rendered `JSON.stringify(dispute, null, 2)` into a
 * `<pre>`. That is a developer reading a record, not an operator working a
 * case, and it put raw fields on screen for a surface that is meant to be
 * usable without reading database rows — ADM-02-FR-05 in as many words.
 *
 * Rejecting a refund took its reason through `window.prompt`, beside an
 * approve button that needed none. The asymmetry is worth removing: the
 * decision that denies a family money is the one that should be hardest to
 * take carelessly, not the one behind a modal you dismiss with Escape.
 *
 * And the sections were collapsed by default. On a case view the operator
 * opened deliberately, every section closed means a click before any
 * information — so they are open, and the empty ones say they are empty.
 */

const fmt = (iso?: string): string => (iso ? new Date(iso).toLocaleString('es-MX') : '—');

const ACTION_TONE: Record<string, PillTone> = {
  PENDING_APPROVAL: 'warn',
  APPROVED: 'ok',
  REJECTED: 'bad',
  COMPLETED: 'ok',
  FAILED: 'bad',
};

/** Minimum the API enforces on a reason. */
const MIN_REASON = 5;

const AdminSupportCase: React.FC = () => {
  const { enrollmentId } = useParams<{ enrollmentId: string }>();
  const [data, setData] = useState<SupportCasePayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  /** Keyed by actionId, so two pending approvals do not share one box. */
  const [rejectReason, setRejectReason] = useState<Record<string, string>>({});

  /** DEC-A01-21: the approval awaiting its target + consequence confirmation. */
  const [confirmingApproval, setConfirmingApproval] = useState<string | null>(null);

  const [refund, setRefund] = useState({ amount: '', reason: '', milestoneId: '', notes: '' });
  const [refundError, setRefundError] = useState<string | null>(null);

  const load = async () => {
    if (!enrollmentId) return;
    setError(null);
    try {
      setData(await getSupportCase(enrollmentId));
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar el caso');
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentId]);

  const act = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e: any) {
      setError(e?.message || 'No se pudo completar la acción');
    } finally {
      setBusy(false);
    }
  };

  const submitRefund = async () => {
    setRefundError(null);
    const amount = Number(refund.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      setRefundError('Monto inválido.');
      return;
    }
    if (refund.reason.trim().length < MIN_REASON) {
      setRefundError(`La razón necesita al menos ${MIN_REASON} caracteres.`);
      return;
    }
    await act(async () => {
      await requestRefund({
        enrollmentId: data!.enrollment.enrollmentId,
        milestoneId: refund.milestoneId || undefined,
        amount,
        reason: refund.reason.trim(),
        supportingNotes: refund.notes || undefined,
      });
      setRefund({ amount: '', reason: '', milestoneId: '', notes: '' });
    });
  };

  if (error && !data) {
    return (
      <OpsPage title="Caso de soporte">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }

  if (!data) {
    return (
      <OpsPage title="Caso de soporte">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const e = data.enrollment;
  const pending = data.supportActions.filter((a) => a.status === 'PENDING_APPROVAL').length;

  const actionRow = (a: SupportAction) => {
    const isPending = a.actionType === 'APPROVAL' && a.status === 'PENDING_APPROVAL';
    const reason = rejectReason[a.actionId] ?? '';

    return (
      <div
        key={`${a.actionId}-${a.actionType}`}
        style={{ padding: '12px 0', borderBottom: '1px solid var(--line-2)' }}
      >
        <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Pill tone={ACTION_TONE[a.status] ?? 'neu'}>{a.actionType}</Pill>
          <b>{a.status}</b>
          <span className="mut" style={{ marginLeft: 'auto' }}>
            {fmt(a.createdAt)}
          </span>
        </div>

        <div style={{ marginTop: 6 }}>{a.reason}</div>

        <div className="mut" style={{ marginTop: 4, fontSize: 12 }}>
          {a.actingAdminId}
          {a.amount !== undefined ? ` · ${formatMoney(a.amount, e.currency)}` : ''}
          {a.operationKey ? ` · ${a.operationKey}` : ''}
          {a.refundId ? ` · reembolso ${a.refundId}` : ''}
        </div>

        {isPending ? (
          <div style={{ marginTop: 10 }}>
            {/* The reason sits beside the decision rather than in a prompt. A
                denial should not be easier to take carelessly than an
                approval. */}
            <textarea
              id={`reject-reason-${a.actionId}`}
              className="inp"
              rows={2}
              value={reason}
              aria-label="Razón del rechazo"
              placeholder={`Razón del rechazo (mínimo ${MIN_REASON} caracteres)`}
              onChange={(ev) =>
                setRejectReason((prev) => ({ ...prev, [a.actionId]: ev.target.value }))
              }
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <button
                className="btn btn-d"
                disabled={busy || reason.trim().length < MIN_REASON}
                onClick={() => act(() => rejectRefund(a.actionId, reason.trim()))}
              >
                Rechazar
              </button>
              <button
                className="btn btn-p"
                disabled={busy}
                onClick={() => setConfirmingApproval(a.actionId)}
              >
                Aprobar
              </button>
            </div>
            {confirmingApproval === a.actionId ? (
              <div style={{ marginTop: 10 }} role="group" aria-label="Confirmar la aprobación">
                <Note tone="warn">
                  {`Vas a reembolsar ${
                    a.amount !== undefined ? formatMoney(a.amount, e.currency) : 'este importe'
                  } a la familia de la inscripción ${
                    e.enrollmentId
                  }. El dinero sale al confirmar y no se puede deshacer.`}
                </Note>
                <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-o"
                    disabled={busy}
                    onClick={() => setConfirmingApproval(null)}
                  >
                    Volver
                  </button>
                  <button
                    className="btn btn-p"
                    disabled={busy}
                    onClick={() =>
                      act(async () => {
                        setConfirmingApproval(null);
                        await approveRefund(a.actionId);
                      })
                    }
                  >
                    Confirmar aprobación
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <OpsPage title="Caso de soporte" subtitle={e.enrollmentId}>
      {error ? <Note tone="bad">{error}</Note> : null}
      {pending ? (
        <Note tone="warn">
          {pending} acción{pending > 1 ? 'es' : ''} esperando una segunda aprobación. Quien la
          propuso no puede aprobarla.
        </Note>
      ) : null}

      <KpiRow>
        <Kpi label="Estado" value={e.state} note="De la inscripción" />
        <Kpi label="Total" value={formatMoney(e.total, e.currency)} note="Autorizado" />
        <Kpi
          label="Retención"
          value={formatMoney(e.holdbackAmount, e.currency)}
          note={e.holdbackReleased ? 'Liberada' : 'Retenida'}
          tone={e.holdbackReleased ? 'ok' : 'warn'}
        />
        <Kpi
          label="Reembolsado"
          value={formatMoney(e.refundedAmount, e.currency)}
          note={`Booking fee ${e.bookingFeeReleased ? 'liberado' : 'retenido'}`}
          tone={e.refundedAmount > 0 ? 'lav' : 'neutral'}
        />
      </KpiRow>

      <Section title="Hitos" count={data.milestones.length}>
        {data.milestones.length === 0 ? (
          <Empty icon="📍" title="Sin hitos" />
        ) : (
          <Table
            columns="60px 1fr 140px 120px 170px"
            head={['#', 'Tipo', 'Estado', 'Importe', 'Liberado']}
          >
            {data.milestones.map((m) => (
              <Row key={m.milestoneId}>
                <span className="mut">{m.sequence}</span>
                <span>{m.type}</span>
                <span>{m.status}</span>
                <span>{formatMoney(m.amount, e.currency)}</span>
                <span className="mut">{fmt(m.releasedAt)}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Reservas" count={data.bookings.length}>
        {data.bookings.length === 0 ? (
          <Empty icon="📅" title="Sin reservas" />
        ) : (
          <Table columns="60px 1fr 200px 200px" head={['#', 'Estado', 'Programada', 'Propuesta']}>
            {data.bookings.map((b) => (
              <Row key={b.bookingId}>
                <span className="mut">{b.milestoneSequence}</span>
                <span>{b.state}</span>
                <span className="mut">{fmt(b.scheduledStart)}</span>
                <span className="mut">{b.proposedStart ? fmt(b.proposedStart) : '—'}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Disputas" count={data.disputes.length}>
        {data.disputes.length === 0 ? (
          <Empty icon="⚖️" title="Sin disputas" />
        ) : (
          /* Named fields rather than the JSON dump this used to render. An
             operator working a case should not have to read a record to use
             it — ADM-02-FR-05. */
          data.disputes.map((d: any) => (
            <div key={d.disputeId} style={{ padding: '10px 0' }}>
              <Field label={d.disputeId}>
                <Value>
                  {d.status} · hito {d.milestoneId} · abierta {fmt(d.openedAt)}
                </Value>
              </Field>
              {d.reason ? <Note tone="warn">{d.reason}</Note> : null}
            </div>
          ))
        )}
      </Section>

      <Section
        title="Operaciones de pago"
        count={data.recentPayments.length}
        subtitle="Lo que el ACL de Payments tiene cacheado por clave de operación."
      >
        {data.recentPayments.length === 0 ? (
          <Empty icon="💸" title="Sin operaciones cacheadas" />
        ) : (
          <Table columns="1fr 120px 1fr" head={['Clave', 'Resultado', 'Detalle']}>
            {data.recentPayments.map((p) => (
              <Row key={p.operationKey}>
                <span className="mut" style={{ fontFamily: 'ui-monospace, monospace' }}>
                  {p.operationKey}
                </span>
                <Pill tone={p.success ? 'ok' : 'bad'}>{p.success ? 'Éxito' : 'Fallo'}</Pill>
                <span className="mut">{p.id ?? p.failureMessage ?? '—'}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Notificaciones" count={data.recentNotifications.length}>
        {data.recentNotifications.length === 0 ? (
          <Empty icon="✉️" title="Sin notificaciones" />
        ) : (
          <Table
            columns="1fr 110px 90px 1fr"
            head={['Aviso', 'Entregado', 'Intentos', 'Destinatarios']}
          >
            {data.recentNotifications.map((n) => (
              <Row key={n.notificationId}>
                <span>
                  <b>{n.kind}</b>
                  {n.lastError ? (
                    <>
                      <br />
                      <span className="mut">{n.lastError}</span>
                    </>
                  ) : null}
                </span>
                <Pill tone={n.dispatched ? 'ok' : 'bad'}>{n.dispatched ? 'Sí' : 'No'}</Pill>
                <span>{n.attempts}</span>
                <span className="mut">{n.recipients.join(', ')}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Acciones de soporte" count={data.supportActions.length}>
        {data.supportActions.length === 0 ? (
          <Empty icon="🛠" title="Sin acciones" detail="Nadie ha tocado este caso todavía." />
        ) : (
          data.supportActions.map(actionRow)
        )}
      </Section>

      <Section
        title="Proponer un reembolso"
        subtitle="Por encima del límite de un solo operador, hace falta que un segundo esté de acuerdo."
      >
        <Field label="Importe">
          <input
            id="refund-amount"
            className="inp"
            value={refund.amount}
            aria-label="Monto en dólares"
            placeholder={`Importe en ${e.currency}`}
            onChange={(ev) => setRefund((r) => ({ ...r, amount: ev.target.value }))}
          />
        </Field>

        <Field label="Razón">
          <textarea
            id="refund-reason"
            className="inp"
            rows={3}
            value={refund.reason}
            aria-label="Razón de la acción"
            placeholder={`Mínimo ${MIN_REASON} caracteres. La lee el segundo operador.`}
            onChange={(ev) => setRefund((r) => ({ ...r, reason: ev.target.value }))}
          />
        </Field>

        <Field label="Hito (opcional)">
          <input
            id="refund-milestone"
            className="inp"
            style={{ fontFamily: 'ui-monospace, monospace' }}
            value={refund.milestoneId}
            aria-label="Id de milestone (opcional)"
            placeholder="Todo el ciclo si se deja vacío"
            onChange={(ev) => setRefund((r) => ({ ...r, milestoneId: ev.target.value }))}
          />
        </Field>

        <Field label="Notas internas (opcional)">
          <textarea
            id="refund-notes"
            className="inp"
            rows={2}
            value={refund.notes}
            aria-label="Notas internas (opcional)"
            onChange={(ev) => setRefund((r) => ({ ...r, notes: ev.target.value }))}
          />
        </Field>

        {refundError ? <Note tone="bad">{refundError}</Note> : null}

        <button className="btn btn-p" disabled={busy} onClick={submitRefund}>
          {busy ? 'Enviando…' : 'Proponer reembolso'}
        </button>
      </Section>
    </OpsPage>
  );
};

export default AdminSupportCase;
